import { EditOutlined, SaveOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import ForceButton from '@components/ForceButton';
import type { ColumnsType, PaginatedFilteredTableRef } from '@components/PaginatedFilteredTable';
import PaginatedFilteredTable from '@components/PaginatedFilteredTable';
import { useCooldown } from '@hooks/use-cooldown';
import type { TelegramUser } from '@interfaces/telegram-user';
import ConfirmNavigation from '@pages/components/ConfirmNavigation';
import type { EditableCellProps } from '@pages/Management/EditableCell/EditableCell';
import EditableCell from '@pages/Management/EditableCell/EditableCell';
import type { OnSubmitFunction } from '@pages/Management/hooks/use-editable-form';
import useEditableForm, { useActionRequest } from '@pages/Management/hooks/use-editable-form';
import { isResponse } from '@services/fetcher';
import type { LoaderData } from '@services/types/loader-data';
import { Alert, Badge, Button, Form, message, Space, Tag, Tooltip } from 'antd';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { getFormattedNumber } from 'react-phone-hooks';
import { useLoaderData } from 'react-router-dom';

import { ACTIONS } from './action';
import type { loader } from './loader';
import styles from './TelegramUserManagement.module.scss';

const MILLISECONDS_IN_MINUTE = 60 * 1000;
const NOTIFY_COOLDOWN_IN_MINUTES = 10;
const RESYNC_COOLDOWN_IN_MINUTES = 5;

interface UserForm extends Record<string, unknown> {
  full_name: string;
  phone_number: string | null;
  show_keyboard: boolean;
}

const TelegramUserManagement: FC = () => {
  const loaderData = useLoaderData() as LoaderData<typeof loader>;
  const [data, setData] = useState(loaderData);
  useEffect(() => {
    setData(loaderData);
  }, [loaderData]);

  const tableRef = useRef<PaginatedFilteredTableRef>(null);

  const onSubmit: OnSubmitFunction<TelegramUser> = (params) => {
    const { request, response, error } = params;
    if (error || !response) {
      // Ignore error and request without response, do not process further
      return;
    }

    if (!('show_keyboard' in request)) {
      return;
    }

    const recordId = request['id'];

    if (typeof recordId !== 'number') {
      return;
    }

    setKeyboardChangeUserIds((ids) => {
      if (ids.includes(recordId)) {
        return ids.filter((id) => id !== recordId);
      }

      return [...ids, recordId];
    });
  };

  const { submit: submitAction, isLoadingOrSubmitting: isNotifying } = useActionRequest<
    typeof ACTIONS,
    { userIds: number[] },
    null
  >({
    resetOnSuccess: true,
    onSubmit: ({ action, error, response }) => {
      if (error) {
        // noinspection JSIgnoredPromiseFromCall
        message.error('Failed to notify, retry after some time.');
        return;
      }

      if (action === ACTIONS.Notify && isResponse(response) && response.ok) {
        setKeyboardChangeUserIds([]);
        toggleNotifyCooldown();
      }
    },
  });

  const {
    form,
    isLoadingOrSubmitting,
    isRecordEdited,
    cancelEditing,
    setRecordEditable,
    submitForm,
    submitFunction,
    resetFetcher,
    error,
  } = useEditableForm<TelegramUser, UserForm>(
    'id',
    ['full_name', 'phone_number', 'show_keyboard'],
    'tg-users-fetcher',
    true,
    onSubmit,
  );

  const [isNotifyOnCooldown, toggleNotifyCooldown] = useCooldown(
    'last-menu-notify',
    NOTIFY_COOLDOWN_IN_MINUTES * MILLISECONDS_IN_MINUTE,
  );
  const [keyboardChangeUserIds, setKeyboardChangeUserIds] = useState<number[]>([]);
  const hasUnPushedChanges = keyboardChangeUserIds.length > 0;
  const [isResyncOnCooldown, toggleResyncCooldown] = useCooldown(
    'last-resync',
    RESYNC_COOLDOWN_IN_MINUTES * MILLISECONDS_IN_MINUTE,
  );

  const columns: ColumnsType<TelegramUser> = [
    {
      key: 'full_name',
      title: 'Full Name',
      dataIndex: 'full_name',
      filterable: true,
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'full_name',
        inputType: 'text',
        title: 'Full Name',
        editing: isRecordEdited(record),
        style: { minWidth: '170px' },
      }),
    },
    {
      key: 'telegram_groups.name',
      title: 'Conversation Name(s)',
      dataIndex: 'conversations',
      filterable: true,
      render: (value: string[] | null) =>
        value ? (
          <>
            {value.map((name, index) => (
              <Tag key={index}>{name}</Tag>
            ))}
          </>
        ) : null,
    },
    {
      key: 'phone_number',
      title: 'Phone Number',
      dataIndex: 'phone_number',
      filterable: true,
      width: 230,
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'phone_number',
        inputType: 'phone-number',
        title: 'Phone Number',
        required: false,
        placeholder: '+1 (999) 999-9999',
        editing: isRecordEdited(record),
      }),
      render: (value: string | null) => (value ? getFormattedNumber(value.replace(/^\+/, '')) : null),
    },
    {
      key: 'show_keyboard',
      title: <Tooltip title="Show or hide the Telegram menu (service keyboard) for the user">Telegram Menu</Tooltip>,
      dataIndex: 'show_keyboard',
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'show_keyboard',
        inputType: 'switch',
        title: 'Telegram Menu',
        editing: isRecordEdited(record),
        required: false,
      }),
      render: (_, record) => (
        <Badge status={record.show_keyboard ? 'success' : 'error'} text={record.show_keyboard ? 'Shown' : 'Hidden'} />
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 80,
      render: (_, record) => {
        const editable = isRecordEdited(record);
        return (
          <Space size="middle">
            {editable ? (
              <>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  title="Save"
                  onClick={() => {
                    // noinspection JSIgnoredPromiseFromCall
                    submitForm(record);
                  }}
                />
                <Button
                  type="default"
                  icon={<StopOutlined />}
                  title="Cancel"
                  onClick={() => {
                    resetFetcher();
                    cancelEditing();
                  }}
                />
              </>
            ) : (
              <>
                <Button type="default" icon={<EditOutlined />} onClick={() => setRecordEditable(record)} />
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {error && <Alert message={error} style={{ margin: '24px 0' }} type="error" />}
      <ConfirmNavigation
        needsConfirm={hasUnPushedChanges}
        modalText="You have menu visibility changes that haven't been sent to Telegram yet"
      />
      <div className={styles.topButtons}>
        <Tooltip title="Refresh the conversation associations for all Telegram users (allowed once every 5 minutes)">
          <Button
            icon={isResyncOnCooldown ? <SyncOutlined spin /> : undefined}
            disabled={isLoadingOrSubmitting || isResyncOnCooldown}
            onClick={() => {
              toggleResyncCooldown();
              submitFunction(
                { intent: ACTIONS.ResyncAssociations },
                {
                  method: 'POST',
                  encType: 'application/json',
                },
              );
            }}
          >
            Re-sync user conversations
          </Button>
        </Tooltip>
        <Tooltip
          styles={{ root: { whiteSpace: 'pre-line' } }}
          title={
            hasUnPushedChanges
              ? `Publish pending Telegram menu visibility changes to affected groups (allowed once every ${NOTIFY_COOLDOWN_IN_MINUTES} minutes)`
              : 'No pending Telegram menu visibility changes to publish.\nIf you see a discrepancy, hold Shift and hover to force push updates to all authorized conversations. This is not recommended for routine use because it sends a notification to every authorized group.'
          }
        >
          <ForceButton
            danger={hasUnPushedChanges}
            icon={isNotifyOnCooldown ? <SyncOutlined spin /> : undefined}
            disabled={isNotifyOnCooldown}
            forceableDisabled={!hasUnPushedChanges}
            loading={isNotifying}
            onClick={() => {
              submitAction(
                ACTIONS.Notify,
                {
                  userIds: keyboardChangeUserIds,
                },
                { method: 'POST' },
              );
            }}
          >
            Publish changes to Telegram
          </ForceButton>
        </Tooltip>
      </div>
      <Form form={form} component={false}>
        <PaginatedFilteredTable<TelegramUser>
          ref={tableRef}
          data={data}
          columns={columns}
          rowKey="id"
          loading={isLoadingOrSubmitting}
          rowClassName="editable-row"
          components={{ body: { cell: EditableCell } }}
        />
      </Form>
    </>
  );
};

export default TelegramUserManagement;
